import { capitalizeFirstLetter } from '@silverfort-nodelibs/common/utils/common';
import { RISK_LEVELS, SERVICE_ACCOUNT_ACTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { ColumnFilter } from '../../ServiceAccountsTypes';

const ADDITIONAL_FILTERS = ['insights', 'owner', 'custom_owner', 'smart_policies'];

const SERVICE_ACCOUNT_INSIGHTS_FILTER_OPTIONS = [
    { title: 'Domain Admins', value: 'domain_admins' },
    { title: 'Highly Privileged', value: 'high_privileged' },
    { title: 'Interactive Login', value: 'interactive_login' },
    { title: 'Repetitive Behavior', value: 'is_repetitive' },
    { title: 'From Custom Insight', value: 'custom_insight' },
    { title: 'New Accounts', value: 'new_account' },
    { title: 'Silverfort Accounts', value: 'sf_accounts' },
    { title: 'Unassigned Owner', value: 'no_owner' }
];

export const getServiceAccountFiltesWithLicense = (
    itdrEnabled: boolean,
    ispmEnabled: boolean,
    nhiProtectionEnabled: boolean
): Record<string, ColumnFilter> => {
    const insightsFilters = [...SERVICE_ACCOUNT_INSIGHTS_FILTER_OPTIONS];
    if (itdrEnabled) {
        insightsFilters.push({ title: 'Suspected Brute Force', value: 'brute_force' });
    }

    const allFilters: Record<string, ColumnFilter> = {
        insights: {
            title: 'Insights',
            id: 'insights',
            variant: 'filter',
            multiple: true,
            items: insightsFilters,
            showFilter: !ADDITIONAL_FILTERS.includes('insights')
        },
        owner: {
            title: 'Owner by AD object',
            id: 'owner',
            variant: 'filter',
            multiple: true,
            items: [],
            searchable: true,
            showFilter: !ADDITIONAL_FILTERS.includes('owner'),
            disableSelectAll: true,
            width: '256px'
        },
        custom_owner: {
            title: 'Owner by Identifier',
            id: 'custom_owner',
            variant: 'filter',
            multiple: true,
            items: [],
            searchable: true,
            showFilter: !ADDITIONAL_FILTERS.includes('custom_owner'),
            disableSelectAll: true,
            width: '248px'
        },
        account_type: {
            title: 'Type',
            id: 'account_type',
            variant: 'filter',
            multiple: true,
            items: [
                { title: 'Managed Service Account', value: 'managed_service_account' },
                { title: 'Service Account', value: 'service_account' },
                { title: 'User', value: 'user' }
            ],
            showFilter: !ADDITIONAL_FILTERS.includes('account_type')
        },
        domain_name: {
            title: 'Domain',
            id: 'domain_name',
            variant: 'filter',
            multiple: true,
            items: [],
            searchable: true,
            showFilter: !ADDITIONAL_FILTERS.includes('domain_name'),
            disableSelectAll: true
        },
        baseline_change: {
            title: 'Baseline change',
            id: 'baseline_change',
            variant: 'filter',
            equation: true,
            equationPlaceholder: 'Days',
            items: [
                {
                    title: 'Greater than',
                    value: '<'
                },
                {
                    title: 'Lower than',
                    value: '>'
                }
            ],
            showFilter: !ADDITIONAL_FILTERS.includes('baseline_change')
        },
        smart_policies: {
            title: 'Smart Policy',
            id: 'smart_policies',
            variant: 'filter',
            multiple: true,
            items: [],
            searchable: true,
            showFilter: !ADDITIONAL_FILTERS.includes('smart_policies'),
            disableSelectAll: true
        }
    };
    if (itdrEnabled || ispmEnabled) {
        allFilters.risk = {
            title: 'Risk',
            id: 'risk',
            variant: 'filter',
            multiple: true,
            items: Object.values(RISK_LEVELS)
                .map(({ label, value }) => ({
                    title: capitalizeFirstLetter(label),
                    value
                }))
                .reverse(),
            showFilter: !ADDITIONAL_FILTERS.includes('risk')
        };
    }
    if (nhiProtectionEnabled) {
        allFilters.is_protected = {
            title: 'Protection',
            id: 'is_protected',
            variant: 'filter',
            items: [
                { title: 'Protected', value: 'true' },
                { title: 'Unprotected', value: 'false' }
            ],
            showFilter: !ADDITIONAL_FILTERS.includes('is_protected'),
            multiple: true
        };
        allFilters.actions = {
            title: 'Policy type',
            id: 'actions',
            variant: 'filter',
            multiple: true,
            items: [
                { title: 'Deny', value: SERVICE_ACCOUNT_ACTIONS.BLOCK.ID },
                { title: 'Notify', value: SERVICE_ACCOUNT_ACTIONS.LOG.ID }
            ],
            showFilter: !ADDITIONAL_FILTERS.includes('actions')
        };
        allFilters.send_to_siem = {
            title: 'Send to SIEM',
            id: 'send_to_siem',
            variant: 'filter',
            multiple: true,
            items: [
                { title: 'Yes', value: 'true' },
                { title: 'No', value: 'false' }
            ],
            showFilter: !ADDITIONAL_FILTERS.includes('send_to_siem')
        };
    }

    return allFilters;
};

export const getInsightsOrderWithLicense = (itdrEnabled: boolean) => {
    const insightsOrder = ['service_accounts', 'no_owner', 'domain_admins', 'high_privileged', 'interactive_login'];

    if (itdrEnabled) {
        insightsOrder.push('brute_force');
    }

    return insightsOrder;
};
